import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, Signal, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { UnifiedTreeItem } from '../../../../../../../_shared/models/driver.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

export interface AssetSelection {
  id: number;
  name: string;
  accountName?: string;
}

@Component({
  selector: 'app-compliance-asset-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ScrollingModule, TranslocoDirective],
  templateUrl: './compliance-asset-picker.component.html',
  styleUrl: './compliance-asset-picker.component.scss'
})
export class ComplianceAssetPickerComponent implements OnChanges {
  private readonly transloco = inject(TranslocoService);

  @Input() tree: UnifiedTreeItem[] = [];
  @Input() isLoaded!: Signal<boolean>;
  @Input() labelTitle: string = this.transloco.translate('compliance.library.assetPicker.defaultLabel');
  @Input() assignedAssetIds: number[] = [];
  @Output() selectionChanged = new EventEmitter<AssetSelection[]>();

  searchTerm: string = '';
  hierarchicalTree: UnifiedTreeItem[] = [];
  filteredTree: UnifiedTreeItem[] = [];
  selectedCount: number = 0;
  selectedAssetIds = new Set<number>();
  disabledAssetIds = new Set<number>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignedAssetIds']) {
      this.disabledAssetIds = new Set(this.assignedAssetIds ?? []);
      this.removeDisabledAssetsFromSelection();
      this.emitSelection();
    }

    if (changes['tree'] && this.tree) {
      this.hierarchicalTree = this.tree;
      this.filteredTree = this.flattenTree(this.tree);
      this.removeDisabledAssetsFromSelection();
      this.emitSelection();
    }
  }

  onCheckboxChange(item: UnifiedTreeItem): void {
    if (this.isItemDisabled(item)) return;

    if (item.type === 'asset') {
      this.toggleAsset(item.id);
    } else {
      this.toggleAccount(item);
    }

    this.filteredTree = this.searchTerm
      ? this.searchTree(this.hierarchicalTree, this.searchTerm)
      : this.flattenTree(this.hierarchicalTree);
    this.emitSelection();
  }

  searchAssets(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.filteredTree = this.searchTerm
      ? this.searchTree(this.hierarchicalTree, this.searchTerm)
      : this.flattenTree(this.hierarchicalTree);
  }

  toggleCollapse(item: UnifiedTreeItem): void {
    const original = this.findOriginalById(item.id, item.type, this.hierarchicalTree);
    if (original) original.collapsed = !original.collapsed;
    this.filteredTree = this.flattenTree(this.hierarchicalTree);
  }

  getTotalAssets(item: UnifiedTreeItem): number {
    const count = (nodes: UnifiedTreeItem[]): number =>
      nodes.reduce((sum, n) => sum + (n.type === 'asset' ? 1 : count(n.children ?? [])), 0);
    return count(item.children ?? []);
  }

  trackByTreeItem(index: number, item: UnifiedTreeItem): string {
    return `${item.type}-${item.id}`;
  }

  isAssignedAsset(item: UnifiedTreeItem): boolean {
    return item.type === 'asset' && this.disabledAssetIds.has(item.id);
  }

  isItemDisabled(item: UnifiedTreeItem): boolean {
    if (item.type === 'asset') {
      return this.isAssignedAsset(item);
    }

    return this.getSelectableAssetIds(item).length === 0;
  }

  isItemChecked(item: UnifiedTreeItem): boolean {
    if (item.type === 'asset') {
      return this.isAssignedAsset(item) || this.selectedAssetIds.has(item.id);
    }

    const selectableAssetIds = this.getSelectableAssetIds(item);
    return selectableAssetIds.length > 0 && selectableAssetIds.every(id => this.selectedAssetIds.has(id));
  }

  flattenTree(tree: UnifiedTreeItem[], level: number = 0): UnifiedTreeItem[] {
    const result: UnifiedTreeItem[] = [];
    for (const item of tree) {
      const isParent = !!(item.children?.length);
      result.push({ ...item, hierarchyLevel: level, isParent });
      if (isParent && item.collapsed) continue;
      if (item.children?.length) result.push(...this.flattenTree(item.children, level + 1));
    }
    return result;
  }

  private findOriginalById(id: number, type: string, tree: UnifiedTreeItem[]): UnifiedTreeItem | undefined {
    for (const item of tree) {
      if (item.id === id && item.type === type) return item;
      if (item.children?.length) {
        const found = this.findOriginalById(id, type, item.children);
        if (found) return found;
      }
    }
    return undefined;
  }

  private emitSelection(): void {
    const selected = this.getSelectedAssets(this.hierarchicalTree);
    this.selectedCount = selected.length;
    this.selectionChanged.emit(selected.map(x => ({
      id: x.id,
      name: x.name,
      accountName: this.findAccountName(x, this.hierarchicalTree)
    })));
    this.cdr.detectChanges();
  }

  private toggleAsset(assetId: number): void {
    if (this.selectedAssetIds.has(assetId)) {
      this.selectedAssetIds.delete(assetId);
      return;
    }

    this.selectedAssetIds.add(assetId);
  }

  private toggleAccount(item: UnifiedTreeItem): void {
    const selectableAssetIds = this.getSelectableAssetIds(item);
    const shouldSelect = selectableAssetIds.some(assetId => !this.selectedAssetIds.has(assetId));

    for (const assetId of selectableAssetIds) {
      if (shouldSelect) {
        this.selectedAssetIds.add(assetId);
      } else {
        this.selectedAssetIds.delete(assetId);
      }
    }
  }

  private getSelectableAssetIds(item: UnifiedTreeItem): number[] {
    const result: number[] = [];
    const collect = (nodes: UnifiedTreeItem[]): void => {
      for (const node of nodes) {
        if (node.type === 'asset' && !this.disabledAssetIds.has(node.id)) {
          result.push(node.id);
        }

        if (node.children?.length) {
          collect(node.children);
        }
      }
    };

    if (item.type === 'asset') {
      return this.disabledAssetIds.has(item.id) ? [] : [item.id];
    }

    collect(item.children ?? []);
    return result;
  }

  private getSelectedAssets(tree: UnifiedTreeItem[]): UnifiedTreeItem[] {
    let result: UnifiedTreeItem[] = [];
    for (const item of tree) {
      if (item.type === 'asset' && this.selectedAssetIds.has(item.id) && !this.disabledAssetIds.has(item.id)) {
        result.push(item);
      }
      if (item.children?.length) result = result.concat(this.getSelectedAssets(item.children));
    }
    return result;
  }

  private removeDisabledAssetsFromSelection(): void {
    for (const assetId of this.disabledAssetIds) {
      this.selectedAssetIds.delete(assetId);
    }
  }

  private findAccountName(item: UnifiedTreeItem, tree: UnifiedTreeItem[]): string | undefined {
    let result: string | undefined;
    const traverse = (nodes: UnifiedTreeItem[], parentName?: string) => {
      for (const node of nodes) {
        if (node.id === item.id) { result = parentName; return; }
        if (node.children?.length) traverse(node.children, node.type === 'account' ? node.name : parentName);
      }
    };
    traverse(tree);
    return result;
  }

  private searchTree(items: UnifiedTreeItem[], term: string, level: number = 0): UnifiedTreeItem[] {
    const lower = term.toLowerCase();
    let result: UnifiedTreeItem[] = [];
    for (const item of items) {
      const matched = item.name.toLowerCase().includes(lower);
      const childResults = item.children?.length ? this.searchTree(item.children, term, level + 1) : [];
      if (matched) {
        result.push({ ...item, hierarchyLevel: level, isParent: !!(item.children?.length) });
        if (item.children?.length) result.push(...this.flattenTree(item.children, level + 1));
      } else if (childResults.length) {
        result.push({ ...item, hierarchyLevel: level, isParent: !!(item.children?.length) }, ...childResults);
      }
    }
    return result;
  }
}